import { FormEvent, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from '../lib/api';
import { Alert, Avatar, Button, EmptyState, Field, Input, Loading, RoleBadge } from '../components/ui';

interface Member {
  userId: string;
  email: string;
  fullName: string | null;
  role: 'owner' | 'manager' | 'member';
  canSeeAll: boolean;
  lastLoginAt: string | null;
}
interface Invite { id: string; email: string; role: string; createdAt: string }
interface Roster { members: Member[]; pendingInvites: Invite[]; yourRole: string }

export default function Team() {
  const { me, refresh } = useAuth();
  const [roster, setRoster] = useState<Roster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  const isOwner = me?.role === 'owner';

  async function load() {
    try {
      setRoster(await apiGet<Roster>('/api/group/members'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your team.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function changeRole(userId: string, role: string) {
    setError(null);
    try {
      await apiPatch('/api/group/member', { userId, role });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update role.');
    }
  }

  async function remove(userId: string) {
    setError(null);
    try {
      await apiDelete(`/api/group/member?userId=${encodeURIComponent(userId)}`);
      await load();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove member.');
    }
  }

  if (loading) return <Loading label="Loading your team…" />;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="h-display text-3xl text-soft">Team</h1>
          <p className="mt-1 text-sm text-muted">
            {me?.group?.memberCount === 1
              ? 'A workspace of one. Invite teammates to share your pipeline.'
              : `${me?.group?.memberCount} people in ${me?.group?.name}.`}
          </p>
        </div>
      </div>

      {error && <Alert>{error}</Alert>}

      {isOwner && (
        <InviteForm
          onInvited={(link) => {
            setLastInviteLink(link);
            load();
          }}
        />
      )}

      {lastInviteLink && (
        <Alert kind="success">
          Invite created. Send this link (email delivery lands with the email milestone):{' '}
          <a className="font-semibold underline" href={lastInviteLink}>{lastInviteLink}</a>
        </Alert>
      )}

      {/* Roster */}
      <section>
        <h2 className="font-head text-lg font-semibold text-soft">Members</h2>
        <div className="mt-3 space-y-2">
          {roster?.members.map((m) => (
            <div key={m.userId} className="card flex flex-wrap items-center gap-3 px-4 py-3">
              <Avatar name={m.fullName} email={m.email} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-head font-semibold text-soft">{m.fullName || m.email}</div>
                <div className="truncate text-xs text-muted">{m.email}</div>
              </div>
              <RoleBadge role={m.role} />
              {isOwner && m.userId !== me?.user.id && (
                <div className="flex items-center gap-2">
                  <select
                    value={m.role}
                    onChange={(e) => changeRole(m.userId, e.target.value)}
                    className="rounded-lg border border-line bg-surface-3 px-2 py-1.5 text-sm text-soft outline-none focus:border-power"
                  >
                    <option value="manager">Manager</option>
                    <option value="member">Member</option>
                    <option value="owner">Owner</option>
                  </select>
                  <Button variant="danger" onClick={() => remove(m.userId)} className="px-3 py-1.5 text-sm">
                    Remove
                  </Button>
                </div>
              )}
              {m.userId === me?.user.id && <span className="text-xs text-muted">You</span>}
            </div>
          ))}
        </div>
      </section>

      {/* Pending invites (owner/manager only — server gates the data) */}
      {roster && roster.pendingInvites.length > 0 && (
        <section>
          <h2 className="font-head text-lg font-semibold text-soft">Pending invites</h2>
          <div className="mt-3 space-y-2">
            {roster.pendingInvites.map((i) => (
              <div key={i.id} className="card flex items-center gap-3 px-4 py-3">
                <div className="flex-1 truncate text-sm text-soft">{i.email}</div>
                <RoleBadge role={i.role} />
                <span className="text-xs text-muted">invited</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {!isOwner && roster && roster.members.length === 1 && (
        <EmptyState title="Just you here" body="Only the workspace owner can invite teammates." />
      )}
    </div>
  );
}

function InviteForm({ onInvited }: { onInvited: (link: string) => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await apiPost<{ invitePath: string }>('/api/group/invite', { email: email.trim(), role });
      setEmail('');
      onInvited(window.location.origin + res.invitePath);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card px-4 py-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <Field label="Invite a teammate">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" />
        </Field>
        <Field label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input"
          >
            <option value="member">Member — sees their own book</option>
            <option value="manager">Manager — sees all pipeline</option>
          </select>
        </Field>
        <Button type="submit" loading={busy}>Send invite</Button>
      </div>
      {error && <div className="mt-3"><Alert>{error}</Alert></div>}
    </form>
  );
}
